import { useEffect } from "react";
import { ArrowUpRight, Mic, BookOpen, MapPin, Award } from "lucide-react";
import { Reveal, Overline, MaskedLine } from "@/components/Reveal";
import { trackEvent, QUIZ_URL } from "@/lib/api";

const FOUNDER_IMG = "/images/bisbee-headshot.webp";

const STATS = [
  { value: "40+", label: "Years in golf & performance", id: "stat-years" },
  { value: "50,000+", label: "Golfers coached", id: "stat-golfers" },
  { value: "50+", label: "PGA professionals mentored", id: "stat-pga" },
  { value: "Fortune 500", label: "Leaders advised", id: "stat-f500" },
];

export default function About() {
  useEffect(() => {
    document.title = "About — Wired 2 Win Golf";
    trackEvent("page_view", { page: "about" });
  }, []);

  return (
    <main data-testid="about-page" className="pt-32 lg:pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <Overline className="mb-6"><MaskedLine>About Wired 2 Win</MaskedLine></Overline>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase max-w-4xl">
          <MaskedLine delay={0.15}>Built for the</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-volt">six inches</span></MaskedLine>
          <MaskedLine delay={0.45}>between your ears.</MaskedLine>
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start pb-24 lg:pb-32">
        <Reveal className="lg:col-span-5">
          <div className="border border-white/10 p-2 bg-ink">
            <img src={FOUNDER_IMG} alt="Dave Bisbee, founder of Wired2Win Golf" className="w-full aspect-[4/5] object-cover" />
            <div data-testid="about-founder-caption" className="px-4 py-3 border-t border-white/10 flex items-center gap-2.5">
              <span className="w-2 h-2 bg-volt shrink-0" />
              <p className="text-xs font-bold tracking-wide text-white/70">Dave Bisbee — Founder, Wired2Win Golf</p>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7 space-y-8">
          {[
            { n: "01", t: "Why we exist", d: "Wired 2 Win Golf started with a simple observation: the golfers who improve fastest aren't the ones with the prettiest swings — they're the ones who know themselves best under pressure. We build tools and training for that self-knowledge." },
            { n: "02", t: "What we believe", d: "The mental game isn't mysticism. It's a set of trainable skills: routines, resets, commitment, honest grading. Everything we publish is practical enough to use on your very next round." },
            { n: "03", t: "How it works", d: "Start with the free Wired2Win assessment to map your profile across Motivators, Strengths, Conditions and Stress. Then train your growth zone with weekly drills and resources built for real golfers with real jobs." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="border border-white/10 bg-ink p-8 relative overflow-hidden">
                <span aria-hidden="true" className="absolute -top-4 right-4 font-display font-black text-7xl text-white/5 select-none">{s.n}</span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-3">{s.t}</h2>
                <p className="text-white/60 text-sm sm:text-base leading-relaxed relative">{s.d}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <a
              href={QUIZ_URL}
              data-testid="about-quiz-cta"
              onClick={() => trackEvent("cta_click", { location: "about" })}
              className="group inline-flex items-center gap-2 bg-volt text-obsidian font-display font-bold px-7 py-4 hover:bg-white transition-colors"
            >
              Take the Free Assessment
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* MEET DAVE */}
      <section className="border-t border-white/10 bg-ink/40 relative overflow-hidden" data-testid="founder-section">
        <span aria-hidden="true" className="absolute -top-10 right-0 font-display font-black text-[16rem] lg:text-[24rem] leading-none text-white/[0.03] select-none pointer-events-none">
          DAVE
        </span>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative">
          <Reveal>
            <Overline>Meet the founder</Overline>
            <h2 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase mt-4 max-w-3xl">
              Dave <span className="text-volt">Bisbee</span>
            </h2>
            <p className="mt-4 text-white/50 text-sm font-semibold tracking-wide uppercase">
              Performance Strategist · Creator of MOVE™ &amp; Wired2Win™ · Founder, Back on Course Golf™
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
            <Reveal className="lg:col-span-6" delay={0.1}>
              <div className="space-y-6 text-white/60 text-base sm:text-lg leading-relaxed">
                <p>
                  Dave has spent four decades where golf meets human performance. A nationally recognized performance strategist, he's coached more than 50,000 golfers, mentored over 50 PGA professionals, and advised Fortune 500 leaders — using the game as a lens for leadership, decision-making, and relationships.
                </p>
                <p>
                  Wired2Win™ grew out of that work: a performance and personality model built on Dave's proprietary <span className="text-white font-semibold">G-O-L-F system</span>, mapping your motivators, strengths, ideal conditions, and stress patterns. Paired with his <span className="text-white font-semibold">MOVE™ framework</span> (Motive, Optimism, Vision, Energy), it's the same system he teaches on stages and in boardrooms — distilled here for your game.
                </p>
              </div>
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <BookOpen className="w-5 h-5 text-volt shrink-0" />
                  <span>Best-selling author of <em className="text-white not-italic font-semibold">Back on Course: Drive Business Performance Through Golf</em></span>
                </div>
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <Mic className="w-5 h-5 text-volt shrink-0" />
                  <span>Host of the podcast <em className="text-white not-italic font-semibold">Conversations from the 19th Hole®</em></span>
                </div>
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <Award className="w-5 h-5 text-volt shrink-0" />
                  <span>Keynotes, executive programs and leadership retreats through Back on Course Golf™</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <MapPin className="w-5 h-5 text-volt shrink-0" />
                  <span>Scottsdale, Arizona</span>
                </div>
              </div>
            </Reveal>

            <div className="lg:col-span-5 lg:col-start-8 grid grid-cols-2 gap-4 content-start">
              {STATS.map((s, i) => (
                <Reveal key={s.id} delay={0.15 + i * 0.08}>
                  <div data-testid={s.id} className="border border-white/10 bg-obsidian p-6 h-full hover:border-volt/60 transition-colors duration-300">
                    <p className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-volt leading-none">{s.value}</p>
                    <p className="text-white/50 text-xs font-semibold uppercase tracking-widest mt-3">{s.label}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.5} className="col-span-2">
                <a
                  href={QUIZ_URL}
                  data-testid="founder-quiz-cta"
                  onClick={() => trackEvent("cta_click", { location: "about-founder" })}
                  className="group flex items-center justify-between border border-volt/40 bg-volt/5 p-6 hover:bg-volt/10 transition-colors duration-300"
                >
                  <span className="font-display font-bold text-lg tracking-tight">Discover how Dave's system reads your game</span>
                  <ArrowUpRight className="w-5 h-5 text-volt group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
